import {
  Facture,
  FiltreFactures,
  GenerationFactures,
} from "../types/models/facture";
import { HttpClient } from "./http-client";

/**
 * Factures clients et décomptes chauffeurs.
 *
 * La production (campagne, régénération, cycle de vie) est réservée au
 * back-office ; la consultation est ouverte aux gestionnaires d'entreprise,
 * que le backend filtre sur leur propre entreprise.
 */
export class FactureService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  /** Back-office : suivi de tous les documents, filtré. */
  async getFactures(filtre: FiltreFactures = {}): Promise<Facture[]> {
    return this.http.get<Facture[]>("/api/factures", { query: { ...filtre } });
  }

  /** Documents d'une entreprise — la section « Factures » de sa fiche. */
  async getFacturesByEntreprise(
    idEntreprise: number,
    type?: number,
  ): Promise<Facture[]> {
    return this.http.get<Facture[]>(`/api/factures/entreprise/${idEntreprise}`, {
      query: { type },
    });
  }

  /** Documents de l'entreprise active du compte connecté. */
  async getMesFactures(type?: number): Promise<Facture[]> {
    return this.http.get<Facture[]>("/api/factures/mes-factures", {
      query: { type },
    });
  }

  /** Détail : lignes et historique des versions. */
  async getFactureById(id: number): Promise<Facture> {
    return this.http.get<Facture>(`/api/factures/${id}`);
  }

  /**
   * PDF du document. Sans `version`, c'est le rendu courant ; en précisant une
   * version archivée, on récupère ce qui a réellement été envoyé avant une
   * régénération.
   */
  async getFacturePdf(id: number, version?: number): Promise<Blob> {
    return this.http.getBlob(`/api/factures/${id}/pdf`, { query: { version } });
  }

  /**
   * Lance la facturation d'un mois. Rejouable : une entreprise déjà facturée
   * voit son document régénéré, pas doublé.
   */
  async genererFactures(params: {
    annee: number;
    mois: number;
    type?: number;
    idEntreprise?: number;
  }): Promise<GenerationFactures> {
    return this.http.post<GenerationFactures>("/api/factures/generer", params);
  }

  /**
   * Reconstruit un document après correction d'une course ou d'un tarif
   * chauffeur. Le numéro est conservé, la version précédente est archivée.
   */
  async regenererFacture(id: number): Promise<Facture> {
    return this.http.post<Facture>(`/api/factures/${id}/regenerer`);
  }

  /** Marque le document comme communiqué au destinataire. */
  async emettreFacture(id: number, echeance?: string | null): Promise<void> {
    await this.http.post(`/api/factures/${id}/emettre`, {
      echeance: echeance || null,
    });
  }

  /** Solde le document. Il n'est dès lors plus régénérable. */
  async marquerPayee(id: number): Promise<void> {
    await this.http.post(`/api/factures/${id}/payee`);
  }

  /** Annule le document. Son numéro reste consommé. */
  async annulerFacture(id: number, motif: string): Promise<void> {
    await this.http.post(`/api/factures/${id}/annuler`, { motif });
  }
}
